<script setup lang="ts">
import { computed, inject, ref } from "vue";
import { TranslationService } from "../services/translation.service";
import { OverlaysService } from "../services/overlays.service";
import { EOverlay } from "../EOverlay";
import axios from "axios";
import { SITEURL } from "@libTs/cms.globals";
import { vInputText as vInputTextDir } from "../directives/v-input-text";

const vInputText = vInputTextDir;
const t = (inject<TranslationService>("translationSvc") as TranslationService)
    .t;
const overlaysSvc = inject<OverlaysService>("overlaysSvc") as OverlaysService;

const loginRequest = ref<{ username: string; password: string }>({
    username: "",
    password: "",
});
const isSending = ref(false);
const sendingSuccessful = ref(false);
type sendingStatus =
    | "ok"
    | "error"
    | "set-new-pw-mail"
    | "use-reset-pw"
    | number;
const sendingStatus = ref<sendingStatus | null>(null);

const formPristine = computed(() => {
    return Object.values(loginRequest.value).every(
        (element) => element.length === 0,
    );
});

const formInvalid = computed(() => {
    return (
        formPristine.value ||
        loginRequest.value.username.length === 0 ||
        loginRequest.value.password.length === 0
    );
});

const sendForm = () => {
    if (formInvalid.value || formPristine.value || sendingSuccessful.value) {
        return;
    }

    sendingSuccessful.value = false;
    isSending.value = true;

    axios
        .post<{ username: string } | false | null>(
            SITEURL + "actions/sapShop/user/login",
            loginRequest.value,
            {
                responseType: "json",
                headers: {
                    "Content-Type": "application/json",
                    "X-Requested-With": "XMLHttpRequest",
                },
            },
        )
        .then(
            // success
            (response) => {
                const data = response.data;

                if (
                    data &&
                    typeof data.username === "string" &&
                    data.username.length > 0
                ) {
                    sendingSuccessful.value = true;
                    overlaysSvc.showOverlay(EOverlay.loginSuccessful);
                    console.info("Login successful.");
                    location.reload();
                } else if (data === false) {
                    sendingSuccessful.value = false;
                    sendingStatus.value = 400;
                    console.warn("Error: Invalid login data.");
                } else {
                    sendingSuccessful.value = false;
                    sendingStatus.value = 201;
                    console.info("User ist inaktiv.");
                }
            },

            // error
            (err) => {
                console.error(err);
                sendingSuccessful.value = false;
                sendingStatus.value = 400;
                console.warn("Error: Invalid login data. ");
            },
        )
        .finally(() => {
            isSending.value = false;
        });
};
</script>

<template>
    <form
        id="login-form-overlay"
        name="form"
        @submit.prevent="sendForm()"
        novalidate
    >
        <div class="h2">{{ t("LOGIN") }}</div>
        <p class="mt-10 mb-6">{{ t("LOGIN_INTRO_TEXT") }}</p>

        <div class="mb-6">
            <label class="text-f7">E-Mail</label>
            <input
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s input-text-input mt-2"
                type="email"
                name="username"
                v-model="loginRequest.username"
                :placeholder="t('EMAIL')"
                autocomplete="username"
                required
            />
        </div>
        <div class="">
            <label class="text-f7">Password</label>
            <input
                class="!px-5 !py-3 w-full bg-grey-50 rounded-s input-text-input mt-2"
                type="password"
                name="password"
                v-model="loginRequest.password"
                :placeholder="t('PASSWORD')"
                autocomplete="current-password"
                required
            />
        </div>

        <div>
            <div
                class="font-bold mt-6 cursor-pointer"
                @click="overlaysSvc.showOverlay(EOverlay.passwordForgotten)"
            >
                {{ t("FORGOT_PASSWORD") }}
            </div>
        </div>

        <div class="mt-6" :class="{ 'loading-animation': isSending }">
            <input
                type="submit"
                class="btn-cb"
                :class="{ 'loading-animation': isSending }"
                :disabled="
                    formPristine ||
                    formInvalid ||
                    sendingSuccessful ||
                    isSending
                "
                value="Sign in"
            />
        </div>

        <div
            class="mt-10"
            :style="
                sendingStatus && sendingStatus == 500 ? {} : { display: 'none' }
            "
        >
            <p>{{ t("CONNECTION_ERROR") }}</p>
        </div>
        <div
            class="mt-10 text-red"
            :style="
                sendingStatus && sendingStatus == 400 ? {} : { display: 'none' }
            "
        >
            <p>{{ t("INVALID_LOGIN") }}</p>
        </div>
        <div
            class="mt-10 text-red"
            :style="
                sendingStatus && sendingStatus == 201 ? {} : { display: 'none' }
            "
        >
            <p>{{ t("INACTIVE_USER") }}</p>
        </div>

        <div class="mt-20 mb-4">
            If you don't have an account yet, send us your registration and we
            will open a personal account for you.
        </div>

        <div class="grid-flex flex-left flex-spaced__abs mt-10">
            <div>
                <div
                    class="btn-cb"
                    @click="overlaysSvc.showOverlay(EOverlay.registerForm)"
                >
                    {{ t("REGISTRATION") }}
                </div>
            </div>
        </div>
    </form>
</template>
